/**
 * Survey-responses advanced-filter document — a versioned value the web
 * filter panel (`SurveyResponseFilterPanel`) edits and evaluates CLIENT-SIDE
 * over ONE survey's complete response list (the Responses tab). One schema +
 * ONE evaluator, both in `@releve/shared` (the people/expenses template on
 * `filterDocumentPrimitives.ts`). The Results tab is NOT filtered.
 *
 * The document is AD HOC (component state for the session) and has NO seeded
 * default — `EMPTY_SURVEY_RESPONSE_FILTER_DOCUMENT` is the only start.
 *
 * ── v1 vocabulary ───────────────────────────────────────────────────────────
 *  - `response.source`        string family over `public` / `staff` (never
 *                             NULL, so no "(empty)").
 *  - `response.submitted_on`  DATE family over `submitted_on` — the ORG-ZONE
 *                             calendar day of the `submitted_at` instant,
 *                             stamped by the web mapper (the same day the
 *                             Submitted cell renders through
 *                             `formatInstantInZone`; see `surveyFilters.ts`
 *                             "THE DATE IS AN ORG-ZONE CALENDAR DAY").
 *  - `response.answer`        ANSWER family — the one keyed condition: it
 *                             carries the `question_id` of a CHOICE question
 *                             (`single_choice` / `dropdown` / `multi_choice`,
 *                             see `isSurveyFilterableChoiceQuestion`) and the
 *                             selected OPTION IDS. A response matches when its
 *                             answer to that question contains ANY selected
 *                             option (a single-choice answer is one id, a
 *                             multi-choice answer an id array). An unanswered
 *                             question matches no answer condition. At most
 *                             one condition per question; conditions on
 *                             different questions AND together.
 *
 * Matching is by OPTION ID, never by label: an org may rename an option after
 * responses arrived, and the stored answer keeps the id.
 *
 * The answer family's op is `answer_has_any` — namespaced so the disjoint-op
 * invariant (every evaluator narrows on `op` alone) holds beside the string
 * and date families. The vocabulary has no yes/no type: a yes/no question is
 * authored as a two-option single choice and is covered by that.
 */
import { z } from "zod";
import { makeDateConditionSchema, makeFilterDocumentSchema, makeStringConditionSchema, matchesDateOp, matchesStringOp, toLocalCalendarDay, } from "./filterDocumentPrimitives.js";
// ---------------------------------------------------------------------------
// Field vocabulary (v1)
// ---------------------------------------------------------------------------
export const SURVEY_RESPONSE_FILTER_STRING_FIELDS = ["response.source"];
export const SURVEY_RESPONSE_FILTER_DATE_FIELDS = ["response.submitted_on"];
export const SURVEY_RESPONSE_FILTER_ANSWER_FIELD = "response.answer";
/** The question types whose answers the panel can filter on (option-id answers). */
export const SURVEY_FILTERABLE_CHOICE_TYPES = ["single_choice", "dropdown", "multi_choice"];
export function isSurveyFilterableChoiceQuestion(q) {
    return SURVEY_FILTERABLE_CHOICE_TYPES.includes(q.type);
}
// ---------------------------------------------------------------------------
// Conditions + document
// ---------------------------------------------------------------------------
const stringConditionSchema = makeStringConditionSchema(SURVEY_RESPONSE_FILTER_STRING_FIELDS);
const dateConditionSchema = makeDateConditionSchema(SURVEY_RESPONSE_FILTER_DATE_FIELDS);
/**
 * "Nothing checked" is NO condition — an `answer_has_any` with zero values is
 * rejected (it would match nothing), the same rule as the array family.
 */
const answerConditionSchema = z.object({
    field: z.literal(SURVEY_RESPONSE_FILTER_ANSWER_FIELD),
    op: z.literal("answer_has_any"),
    question_id: z.string().min(1).max(200),
    values: z.array(z.string().min(1).max(200)).min(1).max(50),
});
export const surveyResponseFilterConditionSchema = z.union([
    stringConditionSchema,
    dateConditionSchema,
    answerConditionSchema,
]);
export const surveyResponseFilterDocumentSchema = makeFilterDocumentSchema(surveyResponseFilterConditionSchema);
/** The empty document — "no filters active" (what Clear resets to). */
export const EMPTY_SURVEY_RESPONSE_FILTER_DOCUMENT = {
    version: 1,
    match: "all",
    conditions: [],
};
/** The option ids a stored answer carries — malformed values carry none. */
function answerOptionIds(value) {
    if (typeof value === "string")
        return value === "" ? [] : [value];
    if (Array.isArray(value))
        return value.filter((v) => typeof v === "string");
    return [];
}
function stringValue(row, field) {
    switch (field) {
        case "response.source":
            return row.source ?? "";
    }
}
function dateValue(row, field) {
    switch (field) {
        case "response.submitted_on":
            return toLocalCalendarDay(row.submitted_on);
    }
}
function matchesCondition(row, c) {
    switch (c.op) {
        case "in":
        case "is_empty":
            return matchesStringOp(c.op, stringValue(row, c.field), c.values, c.include_empty);
        case "date_gte":
        case "date_lte":
        case "date_between":
            return matchesDateOp(c.op, dateValue(row, c.field), c.min, c.max);
        case "answer_has_any": {
            const selected = answerOptionIds(row.answers?.[c.question_id]);
            return c.values.some((v) => selected.includes(v));
        }
    }
}
/**
 * Apply a filter document to response rows (AND of all conditions — v1
 * `match` is 'all' only). An empty document returns the input array unchanged
 * (identity — callers can rely on reference equality to skip re-renders).
 */
export function applySurveyResponseFilterDocument(rows, doc) {
    if (doc.conditions.length === 0)
        return rows;
    return rows.filter((row) => doc.conditions.every((c) => matchesCondition(row, c)));
}
/** Count of active conditions — the filter button's badge. */
export function countActiveSurveyResponseConditions(doc) {
    return doc.conditions.length;
}
//# sourceMappingURL=surveyResponseFilters.js.map